// ABOUTME: ui panel for super admins to impersonate users and tenants
// ABOUTME: shows list of users, allows switching between them, and displays current impersonation status

import {
  Close,
  ExitToApp,
  Person,
  PersonAdd,
  Search,
  SupervisorAccount,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  Fab,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setToken, setUser, UserRole } from "../services/authSlice";
import {
  startImpersonation,
  stopImpersonation,
  useGetImpersonatableUsersQuery,
  useStartImpersonatingMutation,
  useStopImpersonatingMutation,
} from "../services/impersonationSlice";
import { RootState } from "../store";
import { getPrimaryRole, hasRole } from "../utils/roleHelpers";

interface ImpersonationUser {
  _id: string;
  roles: UserRole[];
  role?: string; // backward compatibility
  email: string;
  firstName?: string;
  lastName?: string;
  tenantId?: string;
}

const ImpersonationPanel: React.FC = () => {
  const dispatch = useDispatch();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const auth = useSelector((state: RootState) => state.auth);
  const impersonation = useSelector((state: RootState) => state.impersonation);

  // Check if user can impersonate (developer only)
  const canImpersonate =
    hasRole(auth.user?.roles, "developer") ||
    hasRole(impersonation.realUser?.roles, "developer");

  // Skip the query if user cannot impersonate OR if impersonating a user who cannot impersonate
  const shouldSkipQuery =
    !canImpersonate ||
    (impersonation.isImpersonating && !hasRole(auth.user?.roles, "developer"));

  const { data: usersData, isLoading } = useGetImpersonatableUsersQuery(
    undefined,
    { skip: shouldSkipQuery },
  );
  const [startImpersonating] = useStartImpersonatingMutation();
  const [stopImpersonating] = useStopImpersonatingMutation();

  // only show for users who can impersonate (developers only)
  if (!canImpersonate) {
    return null;
  }

  const handleStartImpersonation = async (userId: string) => {
    try {
      const result = await startImpersonating(userId).unwrap();

      // save current user as real user before switching
      dispatch(
        startImpersonation({
          realUser: auth.user!,
          impersonatedUser: result.impersonatedUser,
          originalToken: auth.token!,
        }),
      );

      // update auth state with impersonated user
      dispatch(setToken(result.token));
      dispatch(setUser(result.impersonatedUser));

      setDrawerOpen(false);

      // redirect to appropriate dashboard based on role and tenant
      const user = result.impersonatedUser;
      const tenantSlug = result.tenantSlug || user.tenantSlug;

      if (
        hasRole(user.roles, "developer") ||
        hasRole(user.roles, "tenant-manager")
      ) {
        window.location.href = "/tenant-manager/tenants";
      } else if (tenantSlug) {
        window.location.href = `/${tenantSlug}/orders`;
      } else {
        // fallback to reload if no tenant slug
        window.location.reload();
      }
    } catch (_error) {}
  };

  const handleStopImpersonation = async () => {
    try {
      const result = await stopImpersonating().unwrap();

      // restore original auth state
      dispatch(setToken(result.token));
      dispatch(setUser(result.user));
      dispatch(stopImpersonation());

      // redirect back to tenant management
      window.location.href = "/tenant-manager/tenants";
    } catch (_error) {}
  };

  const filteredUsers = usersData?.users?.filter((user: ImpersonationUser) => {
    const searchLower = searchTerm.toLowerCase();
    const primaryRole = getPrimaryRole(user.roles) || "";
    return (
      user.email.toLowerCase().includes(searchLower) ||
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.lastName?.toLowerCase().includes(searchLower) ||
      primaryRole.toLowerCase().includes(searchLower)
    );
  });

  const getRoleColor = (role: string) => {
    switch (role) {
      case "tenant-owner":
        return "primary";
      case "customer":
        return "default";
      default:
        return "default";
    }
  };

  const getRoleIcon = (role: string) => {
    return role.includes("tenant") ? <SupervisorAccount /> : <Person />;
  };

  return (
    <>
      {/* floating action button to open panel */}
      <Tooltip
        title={
          impersonation.isImpersonating
            ? `Impersonating: ${impersonation.impersonatedUser?.email} (Click to manage)`
            : "Impersonate User"
        }
        placement="left"
      >
        <Fab
          color={impersonation.isImpersonating ? "warning" : "primary"}
          aria-label="impersonation panel"
          onClick={() => setDrawerOpen(true)}
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 1200,
          }}
        >
          <Badge
            color="error"
            variant="dot"
            invisible={!impersonation.isImpersonating}
          >
            {impersonation.isImpersonating ? (
              <SupervisorAccount />
            ) : (
              <PersonAdd />
            )}
          </Badge>
        </Fab>
      </Tooltip>

      {/* impersonation drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 400 } }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">
              {impersonation.isImpersonating
                ? "Impersonation Active"
                : "Impersonate User"}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          {impersonation.isImpersonating ? (
            // When impersonating, only show the stop option
            <Paper
              elevation={2}
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "warning.main",
                color: "warning.contrastText",
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Currently impersonating:
              </Typography>
              <Typography variant="body1" fontWeight="bold" gutterBottom>
                {impersonation.impersonatedUser?.email}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mb: 2 }}>
                Role: {impersonation.impersonatedUser?.role}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                startIcon={<ExitToApp />}
                onClick={handleStopImpersonation}
                sx={{
                  backgroundColor: "rgba(0,0,0,0.2)",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.3)",
                  },
                }}
              >
                Stop Impersonating & Return to{" "}
                {impersonation.realUser?.email ||
                  auth.user?.email ||
                  "Admin Account"}
              </Button>
            </Paper>
          ) : (
            // When not impersonating, show the user list
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Select User to Impersonate:
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              {isLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <List>
                  {filteredUsers?.map((user: ImpersonationUser) => (
                    <ListItem key={user._id} disablePadding>
                      <ListItemButton
                        onClick={() => handleStartImpersonation(user._id)}
                        disabled={
                          impersonation.impersonatedUser?._id === user._id
                        }
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              {getRoleIcon(
                                getPrimaryRole(user.roles) || "customer",
                              )}
                              <span>{user.email}</span>
                            </Box>
                          }
                          secondary={
                            <Box mt={0.5}>
                              <Chip
                                label={getPrimaryRole(user.roles) || "customer"}
                                size="small"
                                color={getRoleColor(
                                  getPrimaryRole(user.roles) || "customer",
                                )}
                                sx={{ mr: 1 }}
                              />
                              {user.firstName && user.lastName && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {user.firstName} {user.lastName}
                                </Typography>
                              )}
                            </Box>
                          }
                          slotProps={{ secondary: { component: "div" } }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default ImpersonationPanel;
